import { Shape, ShapeType } from '@/services/shape';
import {
  getArcLineHeight,
  getCanvasFont
} from '@/watch-renderer/utils/string.utilts';
import { useImagesStore } from '@/store/use-images-store';
import { dataSourceWholeItems } from '@/resources/data-source';
import { useEditorStore } from '@/store/use-editor-store';

const _getTextSize = (shape: Shape) => {
  const {
    fontSize = 0,
    verticalAlign = 'top',
    radius = 0,
    w: w0,
    h: h0,
    style = 'normal',
    fontId = 'misans',
    fontWeight = 'normal'
  } = shape.attrs;
  if (style != 'arc') return { w: w0, h: h0 };

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return { w: 0, h: 0 };

  ctx.textBaseline = 'ideographic';
  ctx.font = getCanvasFont({ fontId, fontSize, fontWeight });
  const measureText = ctx.measureText('ABSCabcd,.-_12239哈哈哈');
  const lineHeight =
    measureText.fontBoundingBoxAscent + measureText.fontBoundingBoxDescent;
  const _lineHeight = getArcLineHeight(lineHeight, verticalAlign);
  const w = (radius + _lineHeight) * 2;
  const h = w;
  return { w, h };
};


/**
 * 获取数字填充图组基于 totalDigits 的最大可能尺寸
 * 解决图组宽度随当前数据值变化导致组尺寸不稳定的问题
 */
const _getImageArrayMaxSize = (
  shape: Shape
): { w: number; h: number } | undefined => {
  const {
    fillType,
    source,
    space = 0,
    decimalOffsetX = 0,
    resources,
    scale: shapeScale = 1
  } = shape.attrs;

  if (fillType !== '数字填充' || !source) return undefined;

  const sourceItem = dataSourceWholeItems.find((item) => item.key === source);
  if (!sourceItem) return undefined;

  const maxValue = sourceItem.maxValue ?? 0;
  const minValue = sourceItem.minValue ?? 0;
  const precision = sourceItem.precision ?? 0;
  const _decimalDigits = shape.attrs.decimalDigits ?? precision;
  const decimalDigits = precision
    ? Math.max(Math.min(precision, _decimalDigits), 0)
    : 0;
  const totalDigits =
    Math.max(String(maxValue).length, String(minValue).length) +
    (decimalDigits > 0 ? decimalDigits + 1 : 0);

  const imageStore = useImagesStore();
  const colorId =
    useEditorStore().themeColor?.id ??
    Object.keys(resources?.images ?? {})[0];
  const imageSrcs: string[] = resources?.images?.[colorId] ?? [];
  let imgW = 0;
  let imgH = 0;
  for (const src of imageSrcs) {
    if (!src) continue;
    const item = imageStore.imagesMap[src];
    if (item?.width) {
      imgW = Math.max(imgW, item.width);
      imgH = Math.max(imgH, item.height ?? 0);
      break;
    }
  }
  if (!imgW) return undefined;

  let w = totalDigits * imgW + (totalDigits - 1) * Number(space);
  if (decimalDigits > 0) w += Number(decimalOffsetX);

  const unitIconSrcs: string[] = resources?.unitIcon?.[colorId] ?? [];
  for (const src of unitIconSrcs) {
    if (!src) continue;
    const item = imageStore.imagesMap[src];
    if (item?.width) {
      w += item.width + Number(space);
      imgH = Math.max(imgH, item.height ?? 0);
      break;
    }
  }

  w = w * shapeScale;
  const h = imgH * shapeScale;
  return { w, h };
};

/**
 * 获取widget的宽高
 * 对数字填充图组使用基于 totalDigits 的最大尺寸，避免随当前数据值变化
 * @param widget
 * @returns
 */
export const getActuallyWidgetSize = (widget: Shape) => {
  let minX = 0;
  let minY = 0;
  let maxX = -Infinity;
  let maxY = -Infinity;
  // contentMinX/Y 记录子元素实际起始位置（不含原点到首个子元素的空白）
  let contentMinX = Infinity;
  let contentMinY = Infinity;

  // 判断当前组是否为 flex 布局，flex 布局下子元素的 x/y 不代表真实位置，应忽略
  const isFlex = widget.isFlexLayout;
  const flexDirection = widget.attrs.flexDirection; // 'row' | 'column'
  const gap = Number(widget.attrs.gap ?? 0);

  // flex 布局下按方向累加计算尺寸
  let flexMainAxis = 0;
  let flexCrossAxis = 0;

  widget.children?.forEach((kid) => {
    const type = kid.type;
    const { x: offsetX = 0, y: offsetY = 0, align } = kid.attrs;

    let width = 0;
    let height = 0;
    if (type == ShapeType.Widget) {
      const size = getActuallyWidgetSize(kid);
      width = size.w;
      height = size.h;
    } else if (type == ShapeType.Text) {
      const size = _getTextSize(kid);
      width = size.w ?? 0;
      height = size.h ?? 0;
    } else if (type == ShapeType.ImageArray) {
      const maxSize = _getImageArrayMaxSize(kid);
      if (maxSize) {
        width = maxSize.w;
        height = maxSize.h;
      } else {
        width = kid.attrs.w ?? 0;
        height = kid.attrs.h ?? 0;
      }
    } else {
      width = kid.attrs.w ?? 0;
      height = kid.attrs.h ?? 0;
    }

    if (isFlex) {
      // flex 布局：忽略子元素 x/y，按排列方向累加宽高
      if (flexDirection === 'row') {
        flexMainAxis += width;
        flexCrossAxis = Math.max(flexCrossAxis, height);
      } else {
        flexMainAxis += height;
        flexCrossAxis = Math.max(flexCrossAxis, width);
      }
    } else {
      // 非 flex 布局：使用子元素 x/y 计算包围盒
      let _offsetX = offsetX;
      if (kid.type == ShapeType.ImageArray && align) {
        _offsetX -= align == 'center' ? width / 2 : align == 'right' ? width : 0;
      }
      minX = Math.min(minX, _offsetX);
      minY = Math.min(minY, offsetY);
      maxX = Math.max(maxX, _offsetX + width);
      maxY = Math.max(maxY, offsetY + height);
      contentMinX = Math.min(contentMinX, _offsetX);
      contentMinY = Math.min(contentMinY, offsetY);
    }
  });

  let w: number;
  let h: number;
  if (isFlex) {
    const childCount = widget.children?.length ?? 0;
    const totalGap = childCount > 1 ? gap * (childCount - 1) : 0;
    if (flexDirection === 'row') {
      w = flexMainAxis + totalGap;
      h = flexCrossAxis;
    } else {
      w = flexCrossAxis;
      h = flexMainAxis + totalGap;
    }
  } else {
    w = maxX == -Infinity ? 100 : maxX - minX;
    h = maxY == -Infinity ? 100 : maxY - minY;
  }

  // contentW/H 是子元素紧凑包围盒的实际尺寸
  const contentW = isFlex ? w : (maxX == -Infinity ? 100 : maxX - contentMinX);
  const contentH = isFlex ? h : (maxY == -Infinity ? 100 : maxY - contentMinY);
  return {
    w,
    h,
    offsetX: isFlex ? 0 : minX,
    offsetY: isFlex ? 0 : minY,
    contentMinX: isFlex ? 0 : (contentMinX == Infinity ? 0 : contentMinX),
    contentMinY: isFlex ? 0 : (contentMinY == Infinity ? 0 : contentMinY),
    contentW,
    contentH
  };
};